define((require) => {
    "use strict";

    const Class                     = require("shared/util/Class");
    const Configuration             = require("shared/services/Configuration");
    const DuplicateExperienceAction = require("editor/ui/actions/DuplicateExperienceAction");
    const ErrorHandler              = require("shared/services/error/ErrorHandler");
    const {Logger}                  = require("shared/util/Logger");
    const Login                     = require("security/Login");
    const Window                    = require("shared/services/Window");

    const log = new Logger("ErrorLogger"); // consistent name for all frontend errors

    // Short-cuts...
    const {Actions, Codes}   = ErrorHandler;

    /**
     * The error page article on the Community, relative to the configured community base url. We use a page
     * for each error code because of Zendesk's poor UI. <p>
     *
     * Keys are <tt>error.code</tt>, see <tt>ErrorHandler.fromHttp<tt> (and elsewhere).
     * @type {Map<string, string>}
     */
    const ERROR_PAGES = new Map();

    ERROR_PAGES.set(Codes.BAD_REQUEST, "201545049");
    ERROR_PAGES.set(Codes.UNSUPPORTED, "201545069");
    ERROR_PAGES.set(Codes.UPLOAD,      "202153305"); // same as "unsupported", but for Images specifically
    ERROR_PAGES.set(Codes.FORBIDDEN,   "201545029");
    ERROR_PAGES.set(Codes.NOT_FOUND,   "201545079");
    ERROR_PAGES.set(Codes.TIMEOUT,     "201545099");
    ERROR_PAGES.set(Codes.VERSIONING,  "201544999");
    ERROR_PAGES.set(Codes.UNKNOWN,     "201545009");
    ERROR_PAGES.set(Codes.UNHANDLED,   "201545019");

    /**
     * A user <i>error action</i> describes a user response to some error action, i.e. "reload", "retry", "duplicate",
     * "community", or "support", or a combination thereof, like the user clicking a support link or doing a manual
     * reload. The actions are defined by <tt>ErrorHandler.Actions</tt>. <p>
     *
     * It is used by the editor only.
     *
     * @class ErrorAction
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const ErrorAction = Class.extend({

        /**
         * @private @type {EditorStatsDAnalytics}
         * The statsD analytics to track when the user clicks to take some recovery action based on an error.
         */
        _statsD: null,

        /**
         * Associate, or not, the supplied statsD client with this error action, to be notified each time
         * an user response to some error action is activated.
         *
         * @param {EditorStatsDAnalytics} statsD The statsD client, if any; can be null/undefined.
         */
        statsD(statsD) {
            this._statsD = statsD;
        },

        /**
         * Returns a callback to activate the user's response to the supplied error, such as performing a "reload" of
         * the model associated with the error, or trying a manual save. <p>
         *
         * If <tt>error.collection</tt> is defined, <tt>forCollection</tt> is called to create the callback,
         * otherwise if <tt>error.model</tt> is defined, <tt>forModel</tt> is called to create the callback;
         * otherwise <tt>forAction</tt> is called.
         *
         * @param  {*}      error    The error in question; cannot be null.
         * @param  {String} [action] The specific action(s) to take, can differ from <tt>error.action</tt>, but
         *                           will default to it.
         *
         * @return {Function} A callback as described to be invoked when the action is to be performed; never null.
         */
        forError(error, action = error.action) {
            if (error.collection) {
                return this.forCollection(error.collection, action);
            }
            if (error.model) {
                return this.forModel(error.model, action);
            }
            return this.forAction(action, error);
        },

        /**
         * Returns a callback to activate the user's response to an error related to the supplied collection.
         * Right now it can only handle a failed fetch for the collection but may be easily extended to support more.
         *
         * Once the callback is activated, the relevant StatsD counter will be incremented, for example
         * "user.action." + action + "." + context, e.g. "user.action.retry.notificationbar".
         *
         * @param   {Collection} collection  The model in question; can be null, in which case <tt>forAction</tt> is
         *                                  used. The error in question is deduced from this model.
         * @param   {String}       [action]  The specific action(s) to take, can differ from <tt>error.action</tt>,
         *                                  but will default to it.
         *
         * @returns {Function} A callback as described to be invoked when the action is to be performed; never null.
         */
        forCollection(collection, action) {
            return (context, done) => {
                if (action.indexOf(Actions.RETRY) >= 0) {
                    const state = collection.syncState.current;
                    // Save a reference to the sync method, since it will/may be updated because of the action...
                    const method = state.method;

                    this._track(`${Actions.RETRY}.${method}`, context);

                    if (method === "fetch") {
                        // following is exactly the same to what we do for models below in `forModel()` method
                        setTimeout(() => { window.location.reload(true); }, 250);  // to allow stats to complete
                        if (done) {
                            done(Actions.RETRY, collection);
                        }
                    } else {
                        log.warn(`unknown method: ${method}`);
                    }
                } else {
                    log.warn(`unknown action: ${action} for collections`);
                }
            };
        },

        /**
         * Returns a callback to activate the user's response to an error related to the supplied model, such
         * as performing a "reload" of the model or trying a manual save. <p>
         *
         * The returned callback has the form <tt>fn(context:String, done:Function, fail:Function)</tt>, where
         * <tt>context</tt> is used to identify the context for statsD, e.g. "notificationbar". <tt>done</tt> and
         * </tt>fail</tt> are callbacks to be invoked on success and failure, respectively, where <tt>done</tt>
         * has the form <tt>fn(action:String, model:ImprovedModel)</tt> and <tt>fail</tt> has the form
         * <tt>fn(action:String, model:ImprovedModel, xhr:*)</tt>.
         *
         * Once the callback is activated, the relevant StatsD counter will be incremented, for example
         * "user.action." + action + "." + context, e.g. "user.action.reload.notificationbar".
         *
         * @param  {ImprovedModel} model    The model in question; can be null, in which case <tt>forAction</tt> is
         *                                  used. The error in question is deduced from this model.
         * @param  {String}        [action] The specific action(s) to take, can differ from <tt>error.action</tt>, but
         *                                  will default to it.
         *
         * @return {Function} A callback as described to be invoked when the action is to be performed; never null.
         */
        forModel(model, action) {
            if (!model) {
                return this.forAction(action);
            }
            const url = window.location.href; // save a reference to ensure we do not reload another url at some point in the future...

            return (context, done, fail) => {
                let     state   = model.syncState.current;
                const   id      = model.get("id");
                let     promise;

                if (!state.error) {
                    state = model.syncState.actual;
                }
                // Use specified action, or default to the one from the sync state of the desired model...
                action = action || ((state.error && state.error.action) || "");
                // Save a reference to the sync method, since it will/may be updated because of the action...
                const method = state.method;

                // Duplicate? For now, we can only copy Experiences...
                if (action.indexOf(Actions.DUPLICATE) !== -1 && model.__typeName === "Experience") {
                    this._track(`${Actions.DUPLICATE}.${method}`, context, id);

                    // We postfix the name of the clone with "copy", not "new" as default, to keep the two
                    // scenarios distinct from a user's point of view...
                    promise = new DuplicateExperienceAction(model, {postfix: "copy"}).execute().done((clone) => {
                        if (done) {
                            done(Actions.DUPLICATE, clone, model);
                        }
                    });

                    if (fail) {
                        promise.fail((xhr) => {
                            fail(Actions.DUPLICATE, model, xhr);
                        });
                    }
                }

                // Reload (or retry)?
                if (action.indexOf(Actions.RELOAD) !== -1) {
                    this._track(`${Actions.RELOAD}.${method}`, context, id);

                    // Try and reload the model, and if that fails, do a full editor reload...
                    promise = model.fetch({triggeredByServerUpdate: true}).fail((xhr) => { // since the change handlers will trigger a save event...
                        if (fail) {
                            fail(Actions.RELOAD, model, xhr);
                        }
                        if (url === window.location.href) {
                            log.error(`Could not reload model, will reload url: <${url}>`);
                            window.location.reload(true);
                        }
                    }).done(() => {
                        if (done) {
                            done(Actions.RELOAD, model);
                        }
                    });

                // Retry? All these actions can fail, of course, but they will already be hooked into using the
                // sync:state life cycle, so it should be fine, i.e. we can end up here again based on a new/
                // duplicate error...
                } else if (action.indexOf(Actions.RETRY) !== -1) {
                    this._track(`${Actions.RETRY}.${method}`, context, id);

                    if (method === "save" || method === model.syncState.alias.save) { // save, or aliased save, like "share"...
                        promise = model.save();
                    } else if (method === "destroy") { // never aliased for now...
                        promise = model.destroy();

                    // We could get an issue if the reload fails, i.e. a fetch fails, so better reload all then. We
                    // do not check the url, we need the entire reload...
                    } else if (method === "fetch") {
                        setTimeout(() => { window.location.reload(true); }, 250); // to allow stats to complete
                        if (done) {
                            done(Actions.RETRY, model);
                        }

                    // Experiences or Group only!
                    } else if (method === "push") {
                        promise = model.push();

                    // Group only!
                    } else if (method === "push-with-assets") {
                        promise = model.push(true); // true = with assets
                    }

                    if (promise) {
                        if (done) {
                            promise.done(() => {
                                done(Actions.RETRY, model);
                            });
                        }
                        if (fail) {
                            promise.fail((xhr) => {
                                fail(Actions.RETRY, model, xhr);
                            });
                        }
                    }
                }

                // Relogin?
                if (action.indexOf(Actions.RELOGIN) !== -1) {
                    this._track(`${Actions.RELOGIN}.${method}`, context, id);
                    promise = Login.relogin();
                    if (done) {
                        promise.done((principal) => {
                            done(Actions.RELOGIN, principal);
                        });
                    }
                    if (fail) {
                        promise.fail((xhr) => {
                            fail(Actions.RELOGIN, xhr);
                        });
                    }
                }

                // Link to community (as well)?
                if (action.indexOf(Actions.COMMUNITY) !== -1) {
                    const key = this._communityLink(state.error); // key will include prefix dot where relevant!
                    this._track(`${Actions.COMMUNITY + key}.${method}`, context, id);
                    if (done) {
                        done(Actions.COMMUNITY, model);
                    }
                }
            };
        },

        /**
         * Returns a callback to activate the user's response for the supplied action(s). <p>
         *
         * The returned callback has the form <tt>fn(context:String, done:Function)</tt>, where
         * <tt>context</tt> is used to identify the context for statsD, e.g. "notificationbar". <tt>done</tt>
         * is a callback to be invoked on success and has the form <tt>fn(action:String)</tt>.
         *
         * @param  {String}  [action] The specific action; will default to the empty string, i.e. do nothing.
         * @param  {*}       [error}  The error in question; optional.
         * @return {Function} A callback as described to be invoked when the action is to be performed; never null.
         */
        forAction(action, error) {
            const url = window.location.href;

            return (context, done, fail) => {
                if (!action) {
                    action = "";
                }
                // Reload? We cannot retry or copy if we do not know the model...
                if (action.indexOf(Actions.RELOAD) !== -1) {
                    this._track(Actions.RELOAD, context);
                    // Do a full reload, allowing the stats to have a bit of time to complete...
                    setTimeout(() => {
                        if (url === window.location.href) {
                            window.location.reload(true);
                        }
                    }, 250);

                    if (done) {
                        done(Actions.RELOAD);
                    }
                }

                // Relogin, i.e. to get latest company state and role..
                if (action.indexOf(Actions.RELOGIN) !== -1) {
                    this._track(Actions.RELOGIN, context);
                    const promise = Login.relogin();
                    if (done) {
                        promise.done((changed) => {
                            done(Actions.RELOGIN, changed[0], changed[1]); // 0: principal, 1 = changed
                        });
                    }
                    if (fail) {
                        promise.fail((xhr) => {
                            fail(Actions.RELOGIN, xhr);
                        });
                    }
                }

                // Show support pop-up (as well)?
                if (action.indexOf(Actions.SUPPORT) !== -1) {
                    this._track(Actions.SUPPORT, context);
                }

                // Link to community (as well)?
                if (action.indexOf(Actions.COMMUNITY) !== -1) {
                    const key = this._communityLink(error);
                    this._track(Actions.COMMUNITY + key, context);
                    if (done) {
                        done(Actions.COMMUNITY);
                    }
                }
            };
        },

        // Opens a link to the community, trying to link to the specific error code if possible...
        _communityLink(error) {
            let url = Configuration.communityBaseUrl;
            let key = "";

            if (url) {
                if (error && error.code) {
                    key = `.${error.code}`; // for statsD
                    // Do we even have a *specific* community link?
                    error = ERROR_PAGES.get(error.code);
                    if (error) {
                        url += `/hc/en-us/articles/${error}`;
                    }
                }
                // Open named window...
                Window.open(url, "zmags_creator_community");
            }
            return key;
        },

        // Logs/tracks the user action...
        _track(action, context, id) {
            if (context) {
                action = `${context}.${action}`;
            }
            const modelId = id || "unknown model id";
            log.info(`User action: <${action}>: <${modelId}> -> ${window.location.href}`);
            if (this._statsD) {
                this._statsD.track(`user_action.${action}${id ? `.${id}` : ""}`);
            }
        }

    });

    return new ErrorAction();
});



// WEBPACK FOOTER //
// ./src/editor/services/error/ErrorAction.js